import { create } from 'zustand';
import { toast } from 'sonner';
import * as feesApi from './feesApi';
import { getApiErrorMessage } from './api';
import type { CreateFeeRecordPayload } from './feesApi';

export interface TimeEntry {
  id: string;
  date: string;
  matterId: string;
  matterLabel: string;
  barrister: string;
  kind: string;
  hours: string;
  amount: number;
  rate: number;
  vat: number;
  status: 'Billed' | 'Billable' | 'Non-billable';
  description: string;
  createdAt: number;
  /** Raw values from the API — what an edit form needs (the fields above are display values). */
  feeType?: string;
  feeDateIso?: string;
  isBillable?: boolean;
  durationMinutes?: number | null;
}

interface TimeEntriesState {
  entries: TimeEntry[];
  loading: boolean;
  fetchEntries: () => Promise<void>;
  addEntry: (payload: CreateFeeRecordPayload) => Promise<TimeEntry>;
  /** Resolves true only when the server accepted the deletion. */
  deleteEntry: (id: string) => Promise<boolean>;
}

export const useTimeEntriesStore = create<TimeEntriesState>()((set, get) => ({
  entries: [],
  loading: false,

  fetchEntries: async () => {
    set({ loading: true });
    try {
      const entries = await feesApi.fetchFeeRecords();
      set({ entries, loading: false });
    } catch (err) {
      console.error('Failed to fetch fee records:', err);
      set({ loading: false });
    }
  },

  addEntry: async (payload) => {
    const entry = await feesApi.createFeeRecord(payload);
    set((s) => ({ entries: [entry, ...s.entries] }));
    return entry;
  },

  /**
   * Delete a time entry, reporting honestly whether the server agreed.
   *
   * This used to be fire-and-forget: it removed the row optimistically, rolled it back on
   * failure and logged to the CONSOLE only, while both call sites announced "Time entry
   * deleted" without waiting. A refused deletion therefore looked successful and the row
   * quietly reappeared — on a barrister's own billable-time record. Now the failure is both
   * SHOWN (so a caller that ignores the result still cannot hide it) and RETURNED (so a caller
   * can withhold its success message). Same shape as `contactsStore.deleteContact`.
   */
  deleteEntry: async (id) => {
    const removed = get().entries.find((e) => e.id === id);
    set((s) => ({ entries: s.entries.filter((e) => e.id !== id) }));
    try {
      await feesApi.deleteFeeRecord(id);
      return true;
    } catch (err) {
      console.error('Failed to delete fee record:', err);
      if (removed) set((s) => ({ entries: [removed, ...s.entries] }));
      toast.error(getApiErrorMessage(err, 'Could not delete this time entry — please try again'));
      return false;
    }
  },
}));
